import React, { useState, useEffect } from 'react';
import { bool, func, object, oneOf, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { camelize } from '../../util/string';
import { pathByRouteName } from '../../util/routes';
import { apiBaseUrl } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  ensureCurrentUser,
  getAlphabetFromValue,
  getAuthPageUserType,
  isUserTypeAdmin,
  isValidUserType,
} from '../../util/data';
import { isSignupEmailTakenError, isTooManyEmailVerificationRequestsError } from '../../util/errors';
import { isEmailNotConfirmedError } from '../../util/supabaseAuth';
import { pickUserFieldsData, addScopePrefix } from '../../util/userHelpers';

import { login, authenticationInProgress, signup, signupWithIdp } from '../../ducks/auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import {
  Page,
  Heading,
  LayoutSingleColumn,
  NamedLink,
  NamedRedirect,
  InlineTextButton,
  SocialLoginButton,
  Modal,
  AuthSection,
  IconClose,
  IconCheckmarkCircle,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';

// We need to get ToS asset and get it rendered for the modal on this page.
import { TermsOfServiceContent } from '../../containers/TermsOfServicePage/TermsOfServicePage';

// We need to get PrivacyPolicy asset and get it rendered for the modal on this page.
import { PrivacyPolicyContent } from '../../containers/PrivacyPolicyPage/PrivacyPolicyPage';

import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { TOS_ASSET_NAME, PRIVACY_POLICY_ASSET_NAME } from './AuthenticationPage.duck';

import { FacebookLogo, GoogleLogo } from './socialLoginLogos';
import css from './AuthenticationPage.module.css';

const getPageLinkProps = (defaultName, userTypeName, userType) => ({
  name: userType ? userTypeName : defaultName,
  ...(userType && { params: { userType } }),
});

// Social login buttons are needed by AuthenticationForms
// export const SocialLoginButtonsMaybe = props => {
//   const routeConfiguration = useRouteConfiguration();
//   const { showFacebookLogin, showGoogleLogin, from, userType } = props;
//   const showSocialLogins = showFacebookLogin || showGoogleLogin;

//   const getDataForSSORoutes = () => {
//     const baseUrl = apiBaseUrl();

//     // Default route where user is returned after successfull authentication
//     const defaultReturn = pathByRouteName('LandingPage', routeConfiguration);

//     // Route for confirming user data before creating a new user
//     const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);

//     const queryParams = new URLSearchParams({
//       ...(defaultReturn ? { defaultReturn } : {}),
//       ...(defaultConfirm ? { defaultConfirm } : {}),
//       // Route where the user should be returned after authentication
//       // This is used e.g. with EditListingPage and ListingPage
//       ...(from ? { from } : {}),
//       // The preselected userType needs to be saved over the visit to identity provider's service
//       ...(userType ? { userType } : {}),
//     });

//     return { baseUrl, queryParams: queryParams.toString() };
//   };

//   const authWithFacebook = () => {
//     const { baseUrl, queryParams } = getDataForSSORoutes();
//     window.location.href = `${baseUrl}/api/auth/facebook?${queryParams}`;
//   };

//   const authWithGoogle = () => {
//     const { baseUrl, queryParams } = getDataForSSORoutes();
//     window.location.href = `${baseUrl}/api/auth/google?${queryParams}`;
//   };

//   return showSocialLogins ? (
//     <div className={css.idpButtons}>
//       <div className={css.socialButtonsOr}>
//         <span className={css.socialButtonsOrText}>
//           <FormattedMessage id="AuthenticationPage.or" />
//         </span>
//       </div>

//       <div className={css.socialButtons}>
//         {showFacebookLogin ? (
//           <SocialLoginButton className={css.socialButton} onClick={() => authWithFacebook()}>
//             {FacebookLogo}
//           </SocialLoginButton>
//         ) : null}

//         {showGoogleLogin ? (
//           <SocialLoginButton className={css.socialButton} onClick={() => authWithGoogle()}>
//             {GoogleLogo}
//           </SocialLoginButton>
//         ) : null}
//       </div>
//     </div>
//   ) : null;
// };

const getNonUserFieldParams = (values, userFieldConfigs) => {
  const userFieldKeys = userFieldConfigs.map(({ scope, key }) => addScopePrefix(scope, key));

  return Object.entries(values).reduce((picked, [key, value]) => {
    const isUserFieldKey = userFieldKeys.includes(key);

    return isUserFieldKey
      ? picked
      : {
          ...picked,
          [key]: value,
        };
  }, {});
};

// Tabs for SignupForm and LoginForm
export const AuthenticationForms = props => {
  const {
    isLogin,
    showFacebookLogin,
    showGoogleLogin,
    userType,
    from,
    submitLogin,
    loginError,
    idpAuthError,
    signupError,
    signupConfirmationEmail,
    authInProgress,
    submitSignup,
    newPasswordRequestedOpen,
    newPasswordSubmittedOpen,
    onCloseNewPasswordRequested,
    onCloseNewPasswordSubmitted,
    termsAndConditions,
  } = props;
  const config = useConfiguration();
  const { userFields, userTypes = [] } = config.user;

  const fromMaybe = from ? { from } : null;

  const handleSubmitSignup = values => {
    const { email, password, fname, lname, displayName, ...rest } = values;
    const displayNameMaybe = displayName ? { displayName: displayName.trim() } : {};

    const alphabet = getAlphabetFromValue(displayName);
    const alphabetMaybe = alphabet ? { alphabet } : {};

    const params = {
      email,
      password,
      firstName: fname.trim(),
      lastName: lname.trim(),
      ...displayNameMaybe,
      publicData: {
        userType,
        ...alphabetMaybe,
        ...pickUserFieldsData(rest, 'public', userType, userFields),
      },
      privateData: {
        ...pickUserFieldsData(rest, 'private', userType, userFields),
      },
      protectedData: {
        ...pickUserFieldsData(rest, 'protected', userType, userFields),
        ...getNonUserFieldParams(rest, userFields),
      },
    };

    submitSignup(params);
  };

  const loginErrorMessage = (
    <div className={css.error}>
      {isEmailNotConfirmedError(loginError) ? (
        <FormattedMessage id="AuthenticationPage.loginFailedEmailNotConfirmed" />
      ) : (
        <FormattedMessage id="AuthenticationPage.loginFailed" />
      )}
    </div>
  );

  const idpAuthErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.idpAuthFailed" />
    </div>
  );

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : isTooManyEmailVerificationRequestsError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedTooManyRequests" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  const formTitle = isLogin ? (
    <Heading as="h1" rootClassName={css.formTitle}>
      <FormattedMessage id="AuthenticationPage.loginTitle" />
    </Heading>
  ) : (
    <Heading as="h1" rootClassName={classNames(css.formTitle, css.capilatizedFormTitle)}>
      <FormattedMessage id="AuthenticationPage.signupTitle" />
    </Heading>
  );

  const formSubTitleLink = isLogin ? (
    <NamedLink
      {...getPageLinkProps('SignupPage', 'SignupForUserTypePage', userType)}
      className={css.formSubTitleLink}
    >
      <FormattedMessage id="AuthenticationPage.signupSubTitleLink" />
    </NamedLink>
  ) : (
    <NamedLink
      {...getPageLinkProps('LoginPage', 'LoginForUserTypePage', userType)}
      className={css.formSubTitleLink}
    >
      <FormattedMessage id="AuthenticationPage.loginSubTitleLink" />
    </NamedLink>
  );
  const formSubTitle = isLogin ? (
    <p className={css.formSubTitle}>
      <FormattedMessage
        id="AuthenticationPage.loginSubTitle"
        values={{
          authLink: formSubTitleLink,
        }}
      />
    </p>
  ) : (
    <p className={css.formSubTitle}>
      <FormattedMessage
        id="AuthenticationPage.signupSubTitle"
        values={{
          authLink: formSubTitleLink,
        }}
      />
    </p>
  );

  const newPasswordRequestedMessage =
    isLogin && newPasswordRequestedOpen ? (
      <div className={css.loginNotice}>
        <IconCheckmarkCircle className={css.loginNoticeCheckmarkIcon} />
        <span className={classNames(css.loginNoticeText, css.loginNoticeTextWitWidth)}>
          <FormattedMessage id="AuthenticationPage.passwordRecoveryRequested" />
        </span>
        <InlineTextButton
          className={css.loginNoticeCloseButton}
          onClick={onCloseNewPasswordRequested}
        >
          <IconClose className={css.loginNoticeCloseIcon} />
        </InlineTextButton>
      </div>
    ) : null;

  const newPasswordSubmittedMessage =
    isLogin && newPasswordSubmittedOpen ? (
      <div className={css.loginNotice}>
        <IconCheckmarkCircle className={css.loginNoticeCheckmarkIcon} />
        <span className={css.loginNoticeText}>
          <FormattedMessage id="AuthenticationPage.passwordRecoverySubmitted" />
        </span>
        <InlineTextButton
          className={css.loginNoticeCloseButton}
          onClick={onCloseNewPasswordSubmitted}
        >
          <IconClose className={css.loginNoticeCloseIcon} />
        </InlineTextButton>
      </div>
    ) : null;

  const loginOrSignupError =
    isLogin && !!idpAuthError
      ? idpAuthErrorMessage
      : isLogin && !!loginError
      ? loginErrorMessage
      : !!signupError
      ? signupErrorMessage
      : null;

  // Supabase with email confirmation: the account exists, the link is on its way.
  if (!isLogin && signupConfirmationEmail) {
    return (
      <div className={css.content}>
        <Heading as="h1" rootClassName={css.formTitle}>
          <FormattedMessage id="AuthenticationPage.signupConfirmTitle" />
        </Heading>
        <p className={css.formSubTitle}>
          <FormattedMessage
            id="AuthenticationPage.signupConfirmText"
            values={{ email: <strong>{signupConfirmationEmail}</strong> }}
          />
        </p>
        <p className={css.formSubTitle}>
          <NamedLink
            {...getPageLinkProps('LoginPage', 'LoginForUserTypePage', userType)}
            className={css.formSubTitleLink}
          >
            <FormattedMessage id="AuthenticationPage.signupConfirmLoginLink" />
          </NamedLink>
        </p>
      </div>
    );
  }

  return (
    <div className={css.content}>
      {newPasswordRequestedMessage}
      {newPasswordSubmittedMessage}
      {loginOrSignupError}
      {formTitle}
      {formSubTitle}

      {isLogin ? (
        <LoginForm
          className={css.loginForm}
          onSubmit={submitLogin}
          inProgress={authInProgress}
          initialValues={{
            userType,
          }}
        />
      ) : (
        <SignupForm
          className={css.signupForm}
          onSubmit={handleSubmitSignup}
          inProgress={authInProgress}
          termsAndConditions={termsAndConditions}
          userTypes={userTypes}
          userFields={userFields}
          initialValues={{
            userType,
          }}
        />
      )}

      {/* <SocialLoginButtonsMaybe
        isLogin={isLogin}
        showFacebookLogin={showFacebookLogin}
        showGoogleLogin={showGoogleLogin}
        userType={userType}
        {...fromMaybe}
      /> */}
    </div>
  );
};

// Form for confirming information from IdP (e.g. Facebook)
// This is shown before new user is created to Marketplace API
const ConfirmIdProviderInfoForm = props => {
  const {
    userType,
    authInfo,
    authInProgress,
    confirmError,
    submitSingupWithIdp,
    termsAndConditions,
  } = props;
  const config = useConfiguration();
  const { userFields, userTypes } = config.user;

  const idp = authInfo ? authInfo.idpId.replace(/^./, str => str.toUpperCase()) : null;

  const handleSubmitConfirm = values => {
    const { idpId, idpToken, email, firstName, lastName } = authInfo;

    const {
      email: newEmail,
      firstName: newFirstName,
      lastName: newLastName,
      displayName,
      userType: userTypeFromForm,
      ...rest
    } = values;

    const displayNameMaybe = displayName ? { displayName: displayName.trim() } : {};
    const submittingUserType = authInfo.userType || userType || userTypeFromForm;

    const alphabet = getAlphabetFromValue(displayName);
    const alphabetMaybe = alphabet ? { alphabet } : {};

    // Pass email, fistName or lastName to Marketplace API only if user has edited them
    // and they can't be fetched directly from idp provider (e.g. Facebook)
    const authParams = {
      ...(newEmail !== email && { email: newEmail }),
      ...(newFirstName !== firstName && { firstName: newFirstName }),
      ...(newLastName !== lastName && { lastName: newLastName }),
    };

    // Pass other values as extended data according to user field configuration
    const extendedDataMaybe = !isEmpty(rest)
      ? {
          publicData: {
            userType: submittingUserType,
            ...alphabetMaybe,
            ...pickUserFieldsData(rest, 'public', userType, userFields),
          },
          privateData: {
            ...pickUserFieldsData(rest, 'private', userType, userFields),
          },
          protectedData: {
            ...pickUserFieldsData(rest, 'protected', userType, userFields),
            // If the confirm form has any additional values, pass them forward as user's protected data
            ...getNonUserFieldParams(rest, userFields),
          },
        }
      : {
          publicData: {
            userType: submittingUserType,
            ...alphabetMaybe,
          },
        };

    submitSingupWithIdp({
      idpToken,
      idpId,
      ...authParams,
      ...displayNameMaybe,
      ...extendedDataMaybe,
    });
  };

  const confirmErrorMessage = confirmError ? (
    <div className={css.error}>
      {isSignupEmailTakenError(confirmError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  ) : null;

  return (
    <div className={css.content}>
      {confirmErrorMessage}
      <Heading as="h1" rootClassName={css.signupWithIdpTitle}>
        <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
      </Heading>
      <p className={css.confirmInfoText}>
        <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
      </p>
      <ConfirmSignupForm
        className={css.form}
        onSubmit={handleSubmitConfirm}
        inProgress={authInProgress}
        termsAndConditions={termsAndConditions}
        authInfo={authInfo}
        idp={idp}
        userTypes={userTypes}
        userFields={userFields}
        initialValues={{
          userType,
        }}
      />
    </div>
  );
};

export const AuthenticationOrConfirmInfoForm = props => {
  const {
    tab,
    userType,
    authInfo,
    from,
    showFacebookLogin,
    showGoogleLogin,
    submitLogin,
    submitSignup,
    submitSingupWithIdp,
    authInProgress,
    loginError,
    idpAuthError,
    signupError,
    signupConfirmationEmail,
    confirmError,
    newPasswordRequestedOpen,
    newPasswordSubmittedOpen,
    onCloseNewPasswordRequested,
    onCloseNewPasswordSubmitted,
    termsAndConditions,
  } = props;
  const isConfirm = tab === 'confirm';
  const isLogin = tab === 'login';

  return isConfirm ? (
    <ConfirmIdProviderInfoForm
      userType={userType}
      authInfo={authInfo}
      submitSingupWithIdp={submitSingupWithIdp}
      authInProgress={authInProgress}
      confirmError={confirmError}
      termsAndConditions={termsAndConditions}
    />
  ) : (
    <AuthenticationForms
      isLogin={isLogin}
      showFacebookLogin={showFacebookLogin}
      showGoogleLogin={showGoogleLogin}
      userType={userType}
      from={from}
      loginError={loginError}
      idpAuthError={idpAuthError}
      signupError={signupError}
      signupConfirmationEmail={signupConfirmationEmail}
      submitLogin={submitLogin}
      authInProgress={authInProgress}
      submitSignup={submitSignup}
      newPasswordRequestedOpen={newPasswordRequestedOpen}
      newPasswordSubmittedOpen={newPasswordSubmittedOpen}
      onCloseNewPasswordRequested={onCloseNewPasswordRequested}
      onCloseNewPasswordSubmitted={onCloseNewPasswordSubmitted}
      termsAndConditions={termsAndConditions}
    />
  );
};

const isNewPasswordRequested = ({ location } = {}) =>
  Boolean(location?.state?.newPasswordRequested);
const isNewPasswordSubmitted = ({ location } = {}) =>
  Boolean(location?.state?.newPasswordSubmitted);

const getAuthInfoFromCookies = () => {
  return Cookies.get('st-authinfo')
    ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
    : null;
};
const getAuthErrorFromCookies = () => {
  return Cookies.get('st-autherror')
    ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
    : null;
};

export const AuthenticationPageComponent = props => {
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [newPasswordRequestedOpen, setNewPasswordRequestedOpen] = useState(
    isNewPasswordRequested(props)
  );
  const [newPasswordSubmittedOpen, setNewPasswordSubmittedOpen] = useState(
    isNewPasswordSubmitted(props)
  );
  const [authInfo, setAuthInfo] = useState(getAuthInfoFromCookies());
  const [authError, setAuthError] = useState(getAuthErrorFromCookies());
  const config = useConfiguration();

  useEffect(() => {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    if (authError) {
      Cookies.remove('st-autherror');
    }
  }, []);

  // On mobile, it's better to scroll to top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tosModalOpen, privacyModalOpen]);

  const {
    authInProgress,
    currentUser,
    intl,
    isAuthenticated,
    location,
    params: pathParams,
    loginError,
    scrollingDisabled,
    signupError,
    signupConfirmationEmail,
    submitLogin,
    submitSignup,
    confirmError,
    submitSingupWithIdp,
    tab,
    onManageDisableScrolling,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
  } = props;

  // History API has potentially state tied to this route
  // We have used that state to store previous URL ("from"),
  // so that use can be redirected back to that page after authentication.
  const locationFrom = location.state?.from || null;
  const authinfoFrom = authInfo?.from || null;
  const from = locationFrom || authinfoFrom || null;

  const isConfirm = tab === 'confirm';

  const { userTypes = [] } = config.user;

  const userType = getAuthPageUserType(config, location, pathParams, authInfo, isConfirm);
  const validUserType = isValidUserType(userTypes, userType);

  const show404 = userType && !validUserType;

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;

  const isLogin = tab === 'login';
  const isAdminUserType = isUserTypeAdmin(userType);

  const onCloseNewPasswordRequested = () => {
    setNewPasswordRequestedOpen(false);
    window.history.replaceState({}, '');
  };
  const onCloseNewPasswordSubmitted = () => {
    setNewPasswordSubmittedOpen(false);
    window.history.replaceState({}, '');
  };

  // Already authenticated, redirect away from auth page
  if (isAuthenticated && from) {
    return <Redirect to={from} />;
  } else if (isAuthenticated && currentUserLoaded) {
    return <NamedRedirect name="LandingPage" />;
  } else if (show404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const marketplaceName = config.marketplaceName;

  const sideTitleMsgId = isLogin
    ? isAdminUserType
      ? 'AuthenticationPage.adminLoginSideTitle'
      : 'AuthenticationPage.loginSideTitle'
    : isAdminUserType
    ? 'AuthenticationPage.adminSignupSideTitle'
    : 'AuthenticationPage.signupSideTitle';

  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { marketplaceName });
  const schemaDescription = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionSignup' }, { marketplaceName });

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn mainColumnClassName={css.layoutWrapperMain} topbar={<TopbarContainer />}>
        <div className={css.root}>
          <AuthSection className={css.authSection} titleMsgId={sideTitleMsgId} />
          <AuthenticationOrConfirmInfoForm
            tab={tab}
            userType={userType}
            authInfo={authInfo}
            from={from}
            showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
            showGoogleLogin={!!process.env.REACT_APP_GOOGLE_CLIENT_ID}
            submitLogin={submitLogin}
            submitSignup={submitSignup}
            submitSingupWithIdp={submitSingupWithIdp}
            authInProgress={authInProgress}
            loginError={loginError}
            idpAuthError={authError}
            signupError={signupError}
            signupConfirmationEmail={signupConfirmationEmail}
            confirmError={confirmError}
            newPasswordRequestedOpen={newPasswordRequestedOpen}
            newPasswordSubmittedOpen={newPasswordSubmittedOpen}
            onCloseNewPasswordRequested={onCloseNewPasswordRequested}
            onCloseNewPasswordSubmitted={onCloseNewPasswordSubmitted}
            termsAndConditions={
              <TermsAndConditions
                onOpenTermsOfService={() => setTosModalOpen(true)}
                onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
                intl={intl}
              />
            }
          />
        </div>
      </LayoutSingleColumn>
      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.termsWrapper}>
          <TermsOfServiceContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.privacyWrapper}>
          <PrivacyPolicyContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
    </Page>
  );
};

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  showSocialLoginsForTests: false,
  privacyAssetsData: null,
  privacyFetchInProgress: false,
  privacyFetchError: null,
  tosAssetsData: null,
  tosFetchInProgress: false,
  tosFetchError: null,
};

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),

  onManageDisableScrolling: func.isRequired,

  // to fetch privacy-policy page asset
  // which is shown in modal
  privacyAssetsData: object,
  privacyFetchInProgress: bool,
  privacyFetchError: propTypes.error,

  // to fetch terms-of-service page asset
  // which is shown in modal
  tosAssetsData: object,
  tosFetchInProgress: bool,
  tosFetchError: propTypes.error,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError, signupConfirmationEmail } = state.auth;
  const { currentUser } = state.user;
  const {
    pageAssetsData: privacyAssetsData,
    inProgress: privacyFetchInProgress,
    error: privacyFetchError,
  } = state.hostedAssets || {};
  const { pageAssetsData: tosAssetsData, inProgress: tosFetchInProgress, error: tosFetchError } =
    state.hostedAssets || {};

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    signupConfirmationEmail,
    confirmError,
    privacyAssetsData,
    privacyFetchInProgress,
    privacyFetchError,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
